import type { UtilitiesExportsMenuItem } from '~/common/posthogEvents';
import type { ExportLabels } from '~/hooks/useExportTerminologyEnabled';

export type ExportsMenuItem = {
  id: UtilitiesExportsMenuItem;
  group: 'data' | 'reports';
  label: string;
  /** How the file reaches the user, shown under the label. */
  hint: string;
  /** Set when the item can't be used; shown in place of the hint. */
  disabledReason?: string;
};

export type ExportsMenuInputs = {
  labels: ExportLabels;
  hasDownloadsFeature: boolean;
  downloadsTurnedOff: boolean;
  hasReportsFeature: boolean;
  canDownloadData: boolean;
  canExportReports: boolean;
  /** Named in the reason when the role can't export, like the app's other "not authorized" messages. */
  roleName?: string;
  // Lite projects get the records research report but not the outreach or communication logs.
  isFullUtilitiesExperience: boolean;
  hasActiveRequest: boolean;
  hasAgencyFiles: boolean;
  // The store keeps the previous project's agencies and requests until this project's load.
  utilitiesLoaded: boolean;
};

export const NOT_IN_PLAN = 'Not included in your plan';
const LOADING = 'Available once utilities finish loading';

export function getExportsMenuItems({
  labels,
  hasDownloadsFeature,
  downloadsTurnedOff,
  hasReportsFeature,
  canDownloadData,
  canExportReports,
  roleName,
  isFullUtilitiesExperience,
  hasActiveRequest,
  hasAgencyFiles,
  utilitiesLoaded,
}: ExportsMenuInputs): ExportsMenuItem[] {
  const NOT_FOR_ROLE = roleName ? `Not available for your role (${roleName})` : 'Not available for your role';
  const reportReason = (needsFullExperience: boolean) => {
    if (needsFullExperience && !isFullUtilitiesExperience) return NOT_IN_PLAN;
    if (!canExportReports) return NOT_FOR_ROLE;
    return undefined;
  };

  // Reports are listed alphabetically.
  const items: ExportsMenuItem[] = [
    {
      id: 'gis_cad_data',
      group: 'data',
      label: 'GIS / CAD data',
      hint: `Opens ${labels.newTab}`,
      disabledReason: !hasDownloadsFeature ? NOT_IN_PLAN : canDownloadData ? undefined : NOT_FOR_ROLE,
    },
    {
      id: 'download_all_files',
      group: 'data',
      label: 'Utility files',
      hint: 'Emailed in ~5 min',
      disabledReason: canDownloadData ? undefined : NOT_FOR_ROLE,
    },
    {
      id: 'communication_log',
      group: 'reports',
      label: 'Communication log',
      hint: 'Emailed in ~5 min',
      disabledReason:
        reportReason(true) ??
        (!utilitiesLoaded ? LOADING : hasActiveRequest ? undefined : 'Available once a request is started'),
    },
    {
      id: 'outreach_log',
      group: 'reports',
      label: 'Outreach log',
      hint: 'Downloads now',
      // Built from the store's agencies, so it waits for this project's data.
      disabledReason: reportReason(true) ?? (utilitiesLoaded ? undefined : LOADING),
    },
    {
      id: 'records_research_report',
      group: 'reports',
      label: 'Records research report',
      hint: 'Emailed in ~5 min',
      disabledReason: reportReason(false),
    },
  ];

  // An admin can turn Utility Reports or Downloads off for an org, which hides them rather than offering an upgrade.
  // "Utility files" shows only on the full experience once files have arrived, matching the table's settings menu.
  return items.filter(
    (item) =>
      (item.group !== 'reports' || hasReportsFeature) &&
      (item.id !== 'gis_cad_data' || !downloadsTurnedOff) &&
      (item.id !== 'download_all_files' || (isFullUtilitiesExperience && utilitiesLoaded && hasAgencyFiles)),
  );
}
